import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { FormData } from '../../../types/transaction';

interface Assistant {
  id: string;
  firstname: string;
  lastname: string;
  agencies: string[];
}

interface AssistantsFormProps {
  formData: FormData;
  onChange: (data: FormData) => void;
}

export default function AssistantsForm({ formData, onChange }: AssistantsFormProps) {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (formData.agencies.some(a => a.name)) {
      loadAssistants();
    } else {
      setAssistants([]);
    }
  }, [formData.agencies]);

  const loadAssistants = async () => {
    try {
      setLoading(true);
      const selectedAgencies = formData.agencies
        .map(a => a.name)
        .filter(Boolean);

      if (selectedAgencies.length === 0) return;

      const { data, error } = await supabase
        .from('assistants')
        .select(`
          id,
          firstname,
          lastname,
          archived,
          assistant_agencies!inner (
            agency
          )
        `)
        .in('assistant_agencies.agency', selectedAgencies)
        .order('lastname')
        .order('firstname');

      if (error) throw error;

      const formattedAssistants = data
        .filter(a => !a.archived)
        .map(a => ({
          id: a.id,
          firstname: a.firstname,
          lastname: a.lastname,
          agencies: a.assistant_agencies.map((aa: any) => aa.agency)
        }));

      setAssistants(formattedAssistants);
    } catch (err: any) {
      console.error('Error loading assistants:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && formData.agencies.some(a => a.name)) {
    return <div>Chargement des assistantes...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">Assistantes</h3>

      <div className="grid grid-cols-2 gap-4">
        {formData.assistants.map((assistant, index) => (
          <div key={index}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assistante {assistant.role === 'entrante' ? 'entrante' : 'sortante'}
            </label>
            <select
              value={assistant.id}
              onChange={(e) => {
                const newAssistants = [...formData.assistants];
                newAssistants[index] = {
                  ...newAssistants[index],
                  id: e.target.value
                };
                onChange({ ...formData, assistants: newAssistants });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              required
            >
              <option value="">Sélectionner une assistante</option>
              {assistants.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.lastname} {a.firstname}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}